<!--
  @component Image component which will try to resize images on server  
-->

<script context="module">
  export const qualityDefaults = { min: 70, max: 95 };
  export const sharpenDefaults = { min: 0.1, max: 0.6 };
</script>

<script lang="ts">
  import { type ImageSizes, sortSizes, getSrcSet } from './image';
  import type { Rounded } from '$mds/types';
  import { createEventDispatcher } from 'svelte';
  import { BREAKPOINTS, STATIC_URL } from '$lib/constants';
  const fallbackImage = `${STATIC_URL}/fallback.png`;

  export let el: HTMLImageElement | null = null;
  /** src: the image source. (if dev environment; srcset wont generate for staging assets) */
  export let src: string | undefined | null = null;
  export let alt: string;
  export let lazy: boolean = true;
  export let decode: boolean = true;
  export let style: string | null = null;
  export let draggable: boolean = true;

  export let border = '';
  export let rounded: Rounded = '';

  /**
   * whether or not to resize the image
   */
  export let resizing = true;
  /**
   * an object of breakpoints and their widths
   * - example: `{ xs: 475, sm: 640, md: 768, lg: 1024 }`
   * - this is defaulted to browser widths but that is probably too large for most images
   *
   * note: srcset wont generate for staging assets
   */
  export let sizes: ImageSizes = { xs: BREAKPOINTS.xs, sm: BREAKPOINTS.sm, md: BREAKPOINTS.md, lg: BREAKPOINTS.lg };
  /**
   * a ratio of width/height
   * - example: 2
   * - example: 0.5
   * - example: 2/1
   *
   * note: use in conjunction with fit=cover to save bytes on resizer
   */
  export let aspect: number | null = null;
  /**
   * a range of qualities to generate the srcsets with
   * - default: `{ min: 65, max: 95 }`
   *
   * note: the smaller the image, the closer to 'max' quality will be used
   */
  export let quality: { min?: number; max?: number } = {};
  /**
   * a range of sharpness to generate the srcsets with
   * - default: `{ min: 0, max: 0.5 }`
   *
   * note: the smaller the image, the closer to 'min' sharpen will be used
   */
  export let sharpen: { min?: number; max?: number } = {};
  /**
   * instruction for image resizer on server.
   *
   * note: can be used to reduce kb on cropped images
   * https://developers.cloudflare.com/images/transform-images/transform-via-url/#fit
   */
  export let fit: 'scale-down' | 'cover' | 'contain' | 'crop' = 'scale-down';
  /**
   * instruction for image resizer on server.
   *
   * note: where to anchor the cropping
   * https://developers.cloudflare.com/images/transform-images/transform-via-url/#gravity
   */
  export let gravity: 'auto' | 'top' | 'bottom' | 'left' | 'right' | 'center' | number = 'center';
  export let format: 'webp' | 'jpg' | 'png' | 'gif' | 'avif' = 'webp';

  export let fadeIn = false;
  export let fallback = fallbackImage;
  export let showFallback: boolean = true;
  let className = '';
  export { className as class };

  let errored = false;
  let srcAttr;
  let srcSetAttr;
  let sizesAttr;
  const dispatch = createEventDispatcher();

  quality.min = quality.min ?? qualityDefaults.min;
  quality.max = quality.max ?? qualityDefaults.max;
  sharpen.min = sharpen.min ?? sharpenDefaults.min;
  sharpen.max = sharpen.max ?? sharpenDefaults.max;

  $: sources = resizing ? getSrcSet(src, sortSizes(sizes), { quality, sharpen, fit, gravity, format, aspect }) : [];
  $: isSourceSet = resizing && sources.length > 0;
  $: loaded = !!el?.complete;

  $: {
    srcSetAttr = null;
    sizesAttr = null;
    srcAttr = src || '';

    if (isSourceSet) {
      srcSetAttr = sources.map(({ srcset }) => srcset).join(', ');
      sizesAttr = sources.map(({ media }) => media).join(', ');
      srcAttr = sources[0]?.srcset?.split(' ')[0];
    }

    if (!srcAttr) {
      style = 'object-fit: cover;';
    }
  }

  const onerror = event => {
    if (showFallback && !errored) {
      event.target.srcset = '';
      event.target.src = fallback;
    } else if (showFallback && errored) {
      event.target.src = fallbackImage;
    }
    errored = true;
    loaded = true;
    dispatch('error');
  };

  const onload = event => {
    dispatch('load');
  };
</script>

<img
  bind:this={el}
  loading={lazy ? 'lazy' : null}
  decoding={decode ? 'async' : null}
  src={srcAttr || fallback}
  srcset={srcSetAttr}
  sizes={sizesAttr}
  class="{className} {border} {rounded} text-[0]"
  class:opacity-0={fadeIn && !loaded}
  class:transition-opacity={fadeIn}
  {style}
  {alt}
  draggable={draggable ? null : false}
  on:load={onload}
  on:error={onerror}
/>
